<template>
  <a-modal
    :visible="props.open"
    width="640px"
    centered
    :footer="false"
    @cancel="handleCancel"
    :bodyStyle="{
      overflow: 'auto'
    }"
    wrapClassName="no-padding"
  >
    <div class="whatsapp-fb-modal">
      <div class="whatsapp-fb-modal__image-wrapper">
        <img src="@/app/channels/components/image/whatsapp_settings.png" alt="whatsapp-user-image" />
      </div>
      <h1 class="whatsapp-fb-modal__title">Finaliza la configuración pendiente de WhatsApp Business en Facebook</h1>
      <p class="whatsapp-fb-modal__description">Ten en cuenta lo siguiente para completar tu configuración con éxito:</p>

      <div class="whatsapp-fb-modal__info-list">
        <div class="whatsapp-fb-modal__info-item" v-for="(item, i) in items" :key="i">
          <component :is="item.icon" class="whatsapp-fb-modal__icon" />
          <span class="whatsapp-fb-modal__info-text" v-html="item.text"></span>
        </div>
      </div>
      <div class="alert-wrapper">
        <a-alert type="info" show-icon>
          <template #icon>
            <VideoLibraryIcon />
          </template>
          <template #message>
            Aprende a integrar tu cuenta en pocos minutos
          </template>
          <template #description>
            Mira nuestro tutorial con el paso a paso.
            <a href="https://www.youtube.com/watch?v=Zu-ilRogMYI&t=68s" target="_blank" rel="noopener noreferrer">
              <span style="display: inline-flex; gap: 2px; align-items: center;">
                Ver videotutorial
                <IconExternalLink color="currentColor" style="width: 14px; " />
              </span>
            </a>
          </template>
        </a-alert>
      </div>
      <div class="whatsapp-fb-modal__wrapper-fb-button">
        <a-button
          class="whatsapp-fb-modal__fb-button"
          type="primary"
          :disabled="embedLink === ''"
          @click="configureWhatsappInFacebook"
        >
          Configurar en Facebook Business Manager <ExternalLinkIcon class="icon icon--fb"/>
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import FacebookIcon from "@/app/common/components/icons/FacebookIcon.vue";
import PhoneIcon from "@/app/common/components/icons/PhoneIcon.vue";
import ExternalLinkIcon from "@/app/common/components/icons/ExternalLinkIcon.vue";
import BriefcaseIcon from "@/app/common/components/icons/BriefcaseIcon.vue";
import TimeIcon from "@/app/common/components/icons/TimeIcon.vue";
import VideoLibraryIcon from "@/app/common/components/icons/VideoLibraryIcon.vue";
import IconExternalLink from "@/app/common/components/atoms/IconExternalLink.vue";
import { InfoCircleFilled, WhatsAppOutlined } from "@ant-design/icons-vue";
import { message, Modal } from "ant-design-vue";
import { createVNode, ref } from 'vue';
import { getEmbedLink } from "@/app/channels/services/whatsappAutoSetup";

const props = defineProps<{
  open: boolean;
}>();

const configurationStarted = ref(false);
const embedLink = ref('');

const items = [
  { text: "El proceso solo se podrá realizar desde la cuenta del administrador de Facebook Business Manager.", icon: FacebookIcon},
  { text: "Autoriza que Gupshup administre tu cuenta de WhatsApp Business.", icon: WhatsAppOutlined},
  { text: "Asegúrate de usar la cuenta donde está registrado el ID de la empresa.", icon: BriefcaseIcon},
  { text: "Verifica que el número a ser integrado no esté vinculado a otra cuenta de WhatsApp Business o personal.", icon: PhoneIcon},
  { text: "Meta dispone de <strong style='font-weight: 600'>máximo 72 horas</strong> para aprobar y activar tu cuenta.", icon: TimeIcon},
];

const emit = defineEmits<{
  (e: "close"): void;
  (e: "refresh"): void;
}>();

const initializeComponent = () => {
  configurationStarted.value = false;
  embedLink.value = '';
}

const handleCancel = () => {
  if (!configurationStarted.value) {
    initializeComponent();
    emit("close");
    return;
  }
  showOpenConfigurationModal();
};

const showOpenConfigurationModal = () => {
  Modal.confirm({
    title: "Al cerrar, se recargará el módulo para actualizar cualquier cambio en el estado de tu solicitud de WhatsApp Business.",
    wrapClassName: "modal-open-configuration",
    icon: createVNode(InfoCircleFilled, {
      style: 'color: #1677ff',
    }),
    okText: "Entendido",
    centered: true,
    onOk() {
      emit("refresh");
      initializeComponent();
    },
    cancelButtonProps: { style: { display: 'none' } } as any
  });
};

const configureWhatsappInFacebook = async () => {
  configurationStarted.value = true;
  window.open(embedLink.value, '_blank', 'noopener,noreferrer');
  embedLink.value = '';
};

const setGupshupAppId = async (IdArg: string) => {
  try {
    const response = await getEmbedLink(IdArg);
    embedLink.value = response.link;
  }
  catch (error) {
    const messageError = "Error al generar el enlace de configuración. Inténtalo más tarde.";
    message.error(messageError);
  }
};

defineExpose({ setGupshupAppId });

</script>
<style lang="sass" scoped>

:global(.no-padding .ant-modal-content)
  padding: 0

.whatsapp-fb-modal
  border-radius: 12px
  background: linear-gradient(180deg, #F7FFF0 0%, #FFFFFF 27%)
  width: 100%
  display: flex
  flex-direction: column
  align-items: center
  text-align: center
  padding: 40px 80px

  font-size: 14px
  line-height: 22px
  letter-spacing: 0

  &__image-wrapper
    text-align: center
    margin-bottom: 16px
    img
      max-width: 88px

  &__title
    font-size: 24px
    line-height: 32px
    font-weight: 600
    color: #000000E0

  &__description
    font-weight: 400
    margin-bottom: 24px
    color: #000000A6

  &__info-list
    display: flex
    flex-direction: column
    gap: 12px
    width: 100%
    max-width: 480px
    margin-bottom: 16px

  &__info-item
    display: flex
    align-items: flex-start
    gap: 8px
    text-align: left

  &__icon
    flex-shrink: 0
    width: 20px
    height: 20px
    display: flex
    align-items: center
    justify-content: center
    margin-top: 3px
    :deep(svg)
      color: #000000A6
      width: 17.5px
      height: 17.5px

  &__info-text
    max-width: 452px
    font-weight: 400
    color: #000000A6

  &__wrapper-fb-button
    margin-top: 16px
    width: 480px
    height: 40px
  &__fb-button
    font-size: 16px
    height: 100%
    width: 100%
    display: inline-flex
    align-items: center
    justify-content: center
    
  .alert-wrapper
    width: 480px

  :deep(.ant-alert)
    width: 100%
    height: 70px
    text-align: left
    padding-top: 12px
    padding-bottom: 12px

  :deep(.ant-alert-icon)
    transform: translateY(50%)

  :deep(.ant-alert-message)
    margin-bottom: 2px
    font-size: 14px
    font-weight: 600
    line-height: 22px

:global(.modal-open-configuration .ant-modal-confirm-body)
  align-items: flex-start !important
:global(.modal-open-configuration .anticon)
  margin-top: 3px !important
</style>